<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <!-- <h3>Event History</h3>
        <p class="text-muted">View the full history of all key events on this workflow.</p> -->

        <div v-if="loading" key="loadingRecords" class="min-height-300 d-flex align-items-center justify-content-center">
            <busy-spinner text="Loading..."></busy-spinner>
        </div>

        <div 
            v-else-if="!loading && !visibleEvents.length" 
            key="noRecords" 
            class="d-flex align-items-center justify-content-center min-height-300"
        >
            <div class="text-muted font-weight-bold">
                No History To Show
            </div>
        </div>

        <div
            v-else-if="!loading && visibleEvents.length"
            key="hasRecords"
            class="w-100"
        >
            <div class="text-center font-italic font-size-80 text-muted">Most recent events shown first</div>
            <Timeline 
                :value="visibleEvents" 
                dataKey="id" 
                class="flow-event-history-timeline my-4"
                :class="showOpposite ? 'show-opposite' : 'hide-opposite'"
            >
                
                <template #opposite="slotProps">
                    <div v-if="showOpposite" class="timeline-opposite-wrapper pb-4">
                        <div class="font-size-90 text-muted">{{slotProps.item.date_formatted}}</div>
                        <div class="font-size-80 text-muted">{{slotProps.item.time_formatted}}</div>
                        <div class="font-size-70 text-muted">{{slotProps.item.time_ago}}</div>
                    </div>
                </template>

                <template #marker="slotProps">
                    <div class="p-timeline-event-marker" :class="slotProps.item.markerClasses"></div>
                    <!-- <div v-if="slotProps.index !== (visibleEvents.length - 1)" class="p-timeline-event-connector"></div> -->
                </template>

                <template #content="slotProps">  <!-- use anything in here from slotProps.items -->
                    <div 
                        v-if="!showOpposite"
                        class="line-height-100 mb-1"
                    >
                        <div class="font-size-80 text-muted">
                            <span class="">{{slotProps.item.date_formatted}}</span>
                            <span class="mx-1">&middot;</span>
                            <span class="">{{slotProps.item.time_formatted}}</span>
                            <!-- <span class="mx-1">&middot;</span>
                            <span class="">{{slotProps.item.zone_formatted}}</span> -->
                            <span class="mx-2">&middot;</span>
                            <span class="">{{slotProps.item.time_ago}}</span>
                        </div>
                    </div>
                    <div class="timeline-content-wrapper pb-2rem line-height-100">
                        <div class="font-weight-400">
                            <span v-if="slotProps.item.showActor && slotProps.item.isNodeEvent" class="font-weight-600">{{ slotProps.item.actor.full_name }}</span>
                            <!-- <span v-if="slotProps.item.isWorkflowReset">
                                <span class="font-italic">reset the workflow</span>
                                back to task
                            </span>
                            <span v-else-if="slotProps.item.isApprovalEvent">
                                <span class="font-weight-600 font-italic" :class="slotProps.item.wasApproved ? 'text-success-darker' : 'text-danger-darker'">{{ slotProps.item.verb }}</span>
                                the workflow in
                            </span> -->
                            <span v-if="slotProps.item.isNodeEvent">
                                <!-- <span class="font-italic">{{ slotProps.item.verb }}</span>
                                the task -->
                                {{ slotProps.item.nodeEventText }}
                            </span>
                            <span v-if="slotProps.item.taskTitle" class="font-weight-600">
                                <!-- we might have task title, but task was deleted... so only show URL if we still have the task -->
                                <a v-if="slotProps.item.taskUrl" key="hasTaskUrl" :href="slotProps.item.taskUrl" class="text-reset">{{ slotProps.item.taskTitle }}</a>
                                <span v-else key="noTaskUrl">{{ slotProps.item.taskTitle }}</span>
                            </span>
                            <span v-if="slotProps.item.isWorkflowEvent">{{ slotProps.item.workflowEventText }}</span>  <!-- if not hasTask, we'll have this -->
                        </div>
                        <div 
                            v-if="slotProps.item.approvalComment"
                            class="border rounded p-2 m-2 font-size-80 text-show-breaks"
                            :class="slotProps.item.wasApproved ? 'bg-success-50 border-success text-success-darker' : 'bg-danger-50 border-danger text-danger-darker'"
                        >  <!-- Approval Comment -->
                            {{ slotProps.item.approvalComment }}
                        </div>
                        <div v-if="slotProps.item.isFlowStateChange" class="">
                            <span v-if="slotProps.item.showActor" class="">
                                <span class="font-weight-600">{{ slotProps.item.actor.full_name }}</span>
                                changed the status
                            </span>
                            <span v-else class="">Status changed</span>
                            from <b>{{ slotProps.item.oldStateTitle }}</b> &rarr; <b>{{ slotProps.item.newStateTitle }}</b>
                        </div>
                        <div 
                            v-if="slotProps.item.message"
                            class="bg-gray-100 border rounded p-2 m-2 font-size-80 text-show-breaks"
                        >  <!-- MESSAGE -->
                            {{ slotProps.item.message }}
                        </div>
                        <div v-if="$currentUserInfo.user_is_staff" class="font-size-70 text-muted">
                            {{ slotProps.item.isNodeEvent ? ('NODE ' + slotProps.item.task.combined_key) : 'FLOW' }}
                            <span class="mx-2">&middot;</span>
                            {{ slotProps.item.trigger }}
                            <span class="mx-2">&middot;</span>
                            {{ slotProps.item.oldState }} &rarr; {{ slotProps.item.newState }}
                        </div>
                    </div>
                </template>
            </Timeline>
        </div>
        
        <!-- <pre>{{ visibleEvents }}</pre> -->
    
    </div>
</template>

<script>
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import { 
    getFlowObjectApi
} from '../../js/api-service';
import Timeline from 'primevue/timeline';
import { toLuxonDateTime, FormatDateTimeHelper, HumanizeDateTimeHelper } from '../../js/filters-helpers';
import { DateTime } from 'luxon';
import upperFirst from 'lodash/upperFirst';

//Vue.component(Timeline, 'Timeline');


// a hack for now - making lookup of node trigger name to display text 
// TODO TODO TODO - this needs a lot of work to make it generic for all flow objects!
const nodeTriggerLookup = {
    restart_task: 'restarted the task',
    submit_form: 'completed the task',
    clear_task: 'cleared the task',
    complete: 'completed the task',
    approve: 'approved in task',
    reject: 'rejected the flow in task',
    overdue: 'Task became overdue:',
    auto_expire: 'Task expired:',
    manual_expire: 'manually expired the task',
    undo_expire: 'reset expiration of the task',
    //reject_completed: 'was rejected',  // just hide these - already have approval step shown
    assign_internal: 'converted this to an Internal Issue in',
    assign_customer: 'converted this to a Customer Issue in',
    assign_supplier: 'converted this to a Supplier Issue in',
    assign_close: 'closed this workflow in',
    send_reminder: 'sent a reminder for the task',
};


class TimelineRecord {  // put things in right format for Timeline component
    constructor({  // you can set defaults for constructor params here, or just let them be undefined and set in constructor method
        id,
        created,
        verb,  // string
        trigger,  // string
        oldState, // string
        oldStateTitle,  // string, but maybe not provided
        newState, // string
        newStateTitle,  // string, but maybe not provided
        data,  // nested object
        actor,  // will be the TU object
        task,   // will be task object, but maybe with only a few things
        isNodeEvent,  // boolean, its how we tell if this was a task-related thing or not, must use this if we were allowing deleting of tasks (like for rollbacks of template tasks)
        isTuInternal,  // added this manually in component when instantiating
    }) {
        // THINGS DIRECTLY FROM SAVED BACKEND DATA
        this.isVisible = true;  // DEFAULT -- IF YOU FIND SOMETHING ALONG THE WAY WHERE WE DON'T WANT TO SHOW ENTIRE RECORD ON FRONT END, SET THIS TO false
        this.isTuInternal = !!isTuInternal;  // need this to know what URLs to show
        this.id = id;
        this.pk = id;
        this.created = toLuxonDateTime(created);
        this.created_formatted = FormatDateTimeHelper(created);
        this.date_formatted = this.created.toLocaleString(DateTime.DATE_FULL)
        this.time_formatted = this.created.toLocaleString(DateTime.TIME_SIMPLE)
        this.zone_formatted = this.created.toFormat('ZZZZ')  // or 'ZZZZ' or 'ZZZZZ'
        this.time_ago = HumanizeDateTimeHelper(created);
        this.verb = verb ?? '';
        this.trigger = trigger ?? '';
        this.oldState = oldState;
        this.oldStateTitle = oldStateTitle ?? (oldState ? upperFirst(oldState) : '');
        this.newState = newState;
        this.newStateTitle = newStateTitle ?? (newState ? upperFirst(newState) : '');
        this.data = data ?? {};
        this.actor = actor ?? {};
        this.hasActor = !!actor;
        this.showActor = this.hasActor;  // may change later... controls if we should actually SHOW the actor (in case you unexpectedly have it)
        // TASK THINGS
        this.task = task ?? {};
        this.hasTask = !!task;
        // do not use '#' as default for URL -- do that in template if needed -- because we want to be able to check for falsey value in URL
        this.taskUrl = this.isTuInternal ? (this.task?.urls?.internal_detail_in_workflow ?? '') : (this.task?.urls?.external_detail_in_workflow ?? '');
        this.taskTitle = this.task?.title || this.data?.task?.title || '';  // try to get it from task object, but maybe not there if we deleted the task, then try to get from whatever we maybe stashed in data about the task if its there
        this.isNodeEvent = isNodeEvent;
        this.nodeEventText = this.isNodeEvent ? nodeTriggerLookup[this.trigger] ?? `${this.trigger} the task` : '';
        // IF ITS A TASK EVENT, BUT WE DO NOT HAVE TASK TITLE, WE WILL HIDE ENTIRE RECORD (should only happen for old things where we didn't stash task data in 'data')
        if (this.isNodeEvent && !this.taskTitle) {
            this.isVisible = false;
        }
        // APPROVAL THINGS
        this.isApprovalEvent = this.task?.node_type === 'approval';  // you can have 'reject' triggers in non-approval events too... like choices
        this.isRejectTargetEvent = this.trigger === 'reject_completed';  // hide these... already showing rejection not in the approval task
        if (this.isRejectTargetEvent) {
            this.isVisible = false;
        }
        this.isApprovalApproved = this.trigger === 'approve';
        this.isApprovalRejected = this.trigger === 'reject';
        this.wasApproved = this.isApprovalApproved;
        this.approvalComment = (this.isApprovalEvent || this.trigger === 'reject' || this.trigger === 'approve') ? this.data?.message ?? '' : '';
        // MESSAGE THINGS - handle approval messages separately?
        this.message = this.approvalComment ? '' : (this.data?.message || this.data?.comment) ?? '';  // in backend, if you want to show something in message, make sure its top-level in EventRecord.data
        // STYLE THINGS
        this.markerClasses = [];  // add them as you go
        if (this.isApprovalEvent) {
            this.markerClasses.push('is-approval-event');
        }
        if (this.isApprovalApproved) {
            this.markerClasses.push('is-approval-approved');
        }
        if (this.isApprovalRejected) {
            this.markerClasses.push('is-approval-rejected');
        }
        // WORKFLOW RESET
        this.isWorkflowReset = this.data?.trigger_name === 'go_reject_completed';
        if (this.isWorkflowReset) {
            this.markerClasses.push('is-workflow-reset');
        }
        // TOP LEVEL WORKFLOW THINGS
        this.workflowVerb = this.hasTask ? '' : this.verb;  // only for top-level workflow things, keep empty if its a task-level thing
        this.isWorkflowEvent = !this.isNodeEvent;
        this.isFlowStateChange = !this.isNodeEvent && (this.oldState !== this.newState);
        this.isWorkflowCreated = this.workflowVerb === 'created';
        this.isWorkflowStarted = this.workflowVerb === 'started';
        this.isWorkflowCompleted = this.workflowVerb === 'completed';
        if (this.isWorkflowEvent && !this.isFlowStateChange) {
            this.isVisible = false;  // hide extra flow state changes for now - they might happen often
        }
        this.workflowEventText = '';  // just a default... may change below
        if (this.isWorkflowEvent) {

            this.markerClasses.push('is-workflow-event');
            this.markerClasses.push(`is-workflow-${this.workflowVerb}`);            
            // now set 'workflowEventText' -- a full string to display if this is not a task-level event
            switch (this.workflowVerb) {
                case 'created':  // should have actor here...
                    this.workflowEventText = `created the workflow`;  // use with actor in template
                    break;
                case 'started':  // should have actor here...
                    this.workflowEventText = `started the workflow`;  // use with actor in template
                    break;
                case 'completed':  // probably don't have actor here...
                    this.showActor = false;  // force it to not show
                    this.workflowEventText = `The workflow was completed`;
                    break;
                default:
                    this.workflowEventText = '';
                    break;
            }
        }
    }
}

export default {
    name: 'FlowEventHistory',
    components: {
        Timeline,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        flowObjectClass: {
            type: String,
            required: true,
        },
        flowSubtype: {
            type: String,
            default: null,
        },
        flowObjectId: {
            type: String,
            required: true,
        },
        showOpposite: {  // set true to show date/time stuff on opposite side, not in same column as content
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            events: [],  // set ARRAY of events here from API data
            flowStates: [],  // set ARRAY of flow states here from API data
        }
    },
    computed: {
        visibleEvents() {
            // return this.events, but filter our any where isVisible = false
            return this.events.filter(rec => rec.isVisible);
        },
        flowObjectApi() {
            return getFlowObjectApi(this.flowObjectClass, this.flowSubtype);
        },
        flowStateTitlesByName() {
            // return object with keys of state names, values of state objects
            return this.flowStates.reduce((acc, state) => {
                acc[state.name] = state.title;
                return acc;
            }, {});
        },
    },
    mounted() {
        this.getRecords();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getRecords() {
            this.loading = true;
            try {
                const { data } = await this.flowObjectApi.get_event_history(this.flowObjectId);
                this.setLocalData(data);
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false;
        },
        setLocalData(apiData) {
            // set local data from api response data -- will have { states, events }
            const apiRecords = apiData.events;
            this.flowStates = apiData.states;
            const isTuInternal = this.$currentUserInfo.is_internal;  // add this so we know what URLs to show
            this.events = apiRecords.map(ev => {
                //const recordData = {...rec, isTuInternal };
                return new TimelineRecord({
                    id: ev.id,
                    created: ev.created,
                    verb: ev.trigger,  // change later...
                    trigger: ev.trigger,
                    oldState: ev.old_state,
                    oldStateTitle: this.flowStateTitlesByName[ev.old_state],
                    newState: ev.new_state,
                    newStateTitle: this.flowStateTitlesByName[ev.new_state],
                    data: ev.event_kwargs,
                    actor: ev.tenant_user,  // TU object
                    task: ev.flow_node,  // will be null if a FlowObject event
                    isNodeEvent: !!ev.flow_node,
                    isTuInternal: isTuInternal,
                });
            });
        }
    }
}
</script>

<style lang="scss">
// not using scoped so it is easier to control deep components
// instead, just scoping in a custom class for the component
.flow-event-history-timeline.p-timeline.p-timeline-vertical {  // lots of selectors because otherwise standard PrimeVue css specificity wins

    .p-timeline-event-marker {
        border-color: var(--primary-600);
        background-color: var(--primary-100);
        &.is-approval-rejected {
            border-color: var(--danger-600);
            background-color: var(--danger-100);
        }
        &.is-approval-approved {
            border-color: var(--success-600);
            background-color: var(--success-100);
        }
        &.is-workflow-reset {
            border-color: var(--secondary-600);
            background-color: var(--secondary-100);
        }
        &.is-workflow-event {  // filled dark circles for major workflow events
            border-color: var(--gray-900);
            background-color: var(--gray-400);
        }
    }

    &.hide-opposite {
        .p-timeline-event-opposite {  // make sure this is scoped to only this component 
            width: 0;  
            flex-grow: 0;  // need this to prevent it from showing when empty 
            padding: 0;  // it has default padding too...
            //max-width: 300px;
        }
        .p-timeline-event-content {  // make sure this is scoped to only this component 
            // just keep normal
        }
    }
    &.show-opposite {
        .p-timeline-event-opposite {  // make sure this is scoped to only this component 
            width: 25%;  
            //flex-grow: 0;  // need this to prevent it from showing when empty 
            //padding: 0;  // it has default padding too...
            max-width: 200px;
        }
        .p-timeline-event-content {  // make sure this is scoped to only this component 
            width: 75%;
            max-width: 600px;
        }
    }
    
    // .p-timeline-event-content {
    //     width: 75%;
    //     max-width: 600px;
    // }
}


</style>